import React from "react";
import { FormHelperText, ThemeProvider, createTheme } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import styled from "styled-components";
import { Colors, fonts, Spacing } from "../../config";

interface InputDatePickerProps {
  label?: string;
  error?: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  onBlur?: () => void;
  maxDate?: Date;
  readOnly?: boolean;
}

const FieldLabel = styled.p(({ theme }: any) => ({
  fontFamily: fonts.semibold,
  fontWeight: 600,
  fontSize: "12px",
  lineHeight: "15px",
  paddingBottom: "8px",
  margin: 0,
  color: theme.palette.text.primary,
  [theme.breakpoints.down("sm")]: {
    paddingBottom: "5px",
  },
}));

const FieldError = styled(FormHelperText)(() => ({
  color: "red",
  minHeight: Spacing(2.5),
}));

const datePickerTheme = createTheme({
  components: {
    // Outer wrapper — just ensure full width, no background/padding here
    MuiPickersTextField: {
      styleOverrides: {
        root: { width: "100%" },
      },
    },
    // The actual visible input box — pill shape, no underline
    MuiPickersFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: "#F0F0F0",
          borderRadius: "21px",
          paddingBlock: "14px",
          paddingInline: "20px",
          height: "auto",
          "&::before, &::after": { display: "none" },
          "&:hover": { backgroundColor: "#F0F0F0" },
          "&.Mui-focused": { backgroundColor: "#F0F0F0" },
          [theme.breakpoints.down("sm")]: {
            paddingBlock: "10px",
            paddingInline: "15px",
          },
        }),
        sectionsContainer: { padding: 0 },
      },
    },
    // Remove default padding from the date sections list
    MuiPickersSectionList: {
      styleOverrides: { root: { padding: 0 } },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          width: '23px',
          "& .MuiIconButton-root": {
            fontSize: "23px",
            padding: "0px",
            width: '23px',
            "& svg": { fontSize: "20px" },
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          "&.Mui-selected": { backgroundColor: Colors.gray, color: "#FFF" },
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: { root: { backgroundColor: Colors.inputGray } },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { marginInline: "0px !important", marginTop: 3 } },
    },
  },
});

function InputDatePicker({
  label,
  error,
  value,
  onChange,
  onBlur,
  maxDate,
  readOnly
}: InputDatePickerProps) {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <ThemeProvider theme={datePickerTheme}>
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {label && <FieldLabel>{label}</FieldLabel>}
          <DatePicker
            value={value}
            onChange={onChange}
            maxDate={maxDate}
            disabled={readOnly}
            slotProps={{
              textField: {
                variant: "filled",
                fullWidth: true,
                onBlur,
                error: Boolean(error),
                inputProps: { readOnly },
              },
            }}
          />
          <FieldError>{error ?? " "}</FieldError>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default InputDatePicker;
